import type { RandomForest } from "@protontech/ml-inference";
import { newRandomForestInferrer } from "@protontech/ml-inference";

import type { DetectionClass, ModelProvider } from "@protontech/autofill/types";
import type { Result } from "@protontech/autofill/types/utils";
import { unwrapOrThrow } from "@protontech/autofill/types/utils";
import { buildModelProvider } from "../model_provider";
import type { ValidationProblem } from "../validate_weights";
import { validateRandomForestWeights } from "../validate_weights";
import EmailParamsJson from "./params/email-model.json";
import LoginParamsJson from "./params/login-model.json";
import NewPasswordParamsJson from "./params/new-password-model.json";
import OtpParamsJson from "./params/otp-model.json";
import PasswordChangeParamsJson from "./params/password-change-model.json";
import PasswordParamsJson from "./params/password-model.json";
import RecoveryParamsJson from "./params/recovery-model.json";
import RegisterParamsJson from "./params/register-model.json";
import UsernameHiddenParamsJson from "./params/username-hidden-model.json";
import UsernameParamsJson from "./params/username-model.json";

const paramsProvider: Record<DetectionClass, RandomForest> = {
  "new-password": NewPasswordParamsJson,
  "password-change": PasswordChangeParamsJson,
  "username-hidden": UsernameHiddenParamsJson,
  email: EmailParamsJson,
  login: LoginParamsJson,
  otp: OtpParamsJson,
  password: PasswordParamsJson,
  recovery: RecoveryParamsJson,
  register: RegisterParamsJson,
  username: UsernameParamsJson,
};

export const randomForestModelProvider: ModelProvider = unwrapOrThrow(buildModelProvider(paramsProvider, newRandomForestInferrer));

/** Constructs a ModelProvider from a random forest weight set fetched at runtime (e.g. a
 * downloaded model artifact). Returns `ok: false` with the list of problems if the weights don't
 * match the current feature store. */
export const createRandomForestModelProvider = (weights: Record<DetectionClass, RandomForest>): Result<ModelProvider, ValidationProblem[]> => {
  const validation = validateRandomForestWeights(weights);
  if (!validation.ok) return validation;
  return { ok: true, value: unwrapOrThrow(buildModelProvider(weights, newRandomForestInferrer)) };
};
